import React, { useState, useEffect, useCallback } from "react";
import B4RetirementCard from "./B4RetirementCard";
import {
  Section,
  SectionInner,
  Eyebrow,
  H2,
  Lead,
  BtnGhost,
  ProjectGrid,
  B4PageToast,
  B4PageToastActions,
  B4PageToastButton,
} from "./styles";
import { useLanguage } from "../../contexts/LanguageContext";
import { getTranslation } from "../../locales";

const Centered = ({ children, style }) => (
  <div
    style={{
      textAlign: "center",
      padding: "2.5rem 1rem",
      color: "#a9c0cc",
      fontSize: "0.9375rem",
      ...style,
    }}
  >
    {children}
  </div>
);

const Spinner = () => (
  <div
    style={{
      width: 40,
      height: 40,
      margin: "0 auto",
      border: "3px solid rgba(37, 208, 127, 0.25)",
      borderTopColor: "#25d07f",
      borderRadius: "50%",
      animation: "b4spin 0.9s linear infinite",
    }}
  />
);

export default function B4CarbonRetirementsSection({
  retirements = [],
  loading = false,
  error = null,
  onRetry,
}) {
  const { language } = useLanguage();
  const t = getTranslation(language).carbonRetirements;
  const [toast, setToast] = useState(null);

  const dismissToast = useCallback(() => setToast(null), []);

  const handleTxidCopied = useCallback(
    (status) => {
      setToast(status === "success" ? "success" : "error");
    },
    []
  );

  useEffect(() => {
    if (!toast) return undefined;
    const timer = window.setTimeout(() => setToast(null), 10000);
    return () => window.clearTimeout(timer);
  }, [toast]);

  return (
    <>
      <style>
        {`@keyframes b4spin { to { transform: rotate(360deg); } }`}
      </style>
      <Section id="aposentadorias">
        <SectionInner>
          <Eyebrow>{t.eyebrow || "Transparência"}</Eyebrow>
          <H2>{t.title}</H2>
          <Lead style={{ maxWidth: "44rem", marginBottom: "2rem" }}>
            {t.subtitle}
          </Lead>

          {loading && (
            <Centered>
              <Spinner />
              <p style={{ marginTop: "1rem" }}>{t.loading}</p>
            </Centered>
          )}

          {!loading && error && (
            <Centered style={{ color: "#ff8a8a" }}>
              {t.error}: {error}
              <div style={{ marginTop: "1rem" }}>
                <BtnGhost
                  type="button"
                  onClick={() => onRetry?.()}
                  style={{ padding: "0.65rem 1.25rem", fontSize: "0.8125rem" }}
                >
                  {t.tryAgain || "Tentar novamente"}
                </BtnGhost>
              </div>
            </Centered>
          )}

          {!loading && !error && retirements.length > 0 && (
            <ProjectGrid>
              {retirements.map((r) => (
                <B4RetirementCard
                  key={r._id}
                  retirement={r}
                  onTxidCopied={handleTxidCopied}
                />
              ))}
            </ProjectGrid>
          )}

          {!loading && !error && retirements.length === 0 && (
            <Centered>{t.noRetirements}</Centered>
          )}
        </SectionInner>
      </Section>

      {toast === "success" && (
        <B4PageToast role="status" aria-live="polite">
          <strong>{t.txidCopiedTitle}</strong>
          <p>{t.txidCopiedMessage}</p>
          <B4PageToastActions>
            <B4PageToastButton type="button" onClick={dismissToast}>
              {t.dismissToast}
            </B4PageToastButton>
          </B4PageToastActions>
        </B4PageToast>
      )}

      {toast === "error" && (
        <B4PageToast role="alert" aria-live="assertive">
          <strong>{t.txidCopyFailedTitle}</strong>
          <p>{t.txidCopyFailedMessage}</p>
          <B4PageToastActions>
            <B4PageToastButton type="button" onClick={dismissToast}>
              {t.dismissToast}
            </B4PageToastButton>
          </B4PageToastActions>
        </B4PageToast>
      )}
    </>
  );
}
